import { useGetList, useGetRecordId, useRecordContext } from "react-admin";
import type { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { useNavigate } from "react-router";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { PageTitle } from "../../misc/PageTitle";
import { grants } from "../../backoffice.access_control";
import { Row } from "../../misc/Row";
import { EmptyState } from "../../misc/EmptyState";
import { Student } from "../../providers/studentsProvider";
import { DrivingSchoolStudentsList } from "./StudentList";
import { lazy, useMemo } from "react";
import { Badge2 } from "../../misc/Badge2";
import { ResponsiveHashTabs } from "../../misc/ResponsiveHashTabs";

export function DrivingSchoolStudents() {
  const navigate = useNavigate();
  const drivingSchoolId = useGetRecordId();
  const drivingSchool = useRecordContext<DrivingSchool>();
  const studentsWithoutAnInstructorFilter = useMemo(
    () => ({
      drivingSchoolId,
      archived: false,
      status: "active",
      instructorIds: (val: Array<string>) => val.length === 0,
    }),
    [drivingSchoolId],
  );
  const { total: numberOfStudentsWithoutAnInstructor = 0 } = useGetList<Student>(
    "students",
    { filter: studentsWithoutAnInstructorFilter },
    { enabled: !!drivingSchoolId },
  );
  const showVogelFahrschuelerList = grants.has("admin") && drivingSchool?.branches.some((it) => it.vogelId);
  const tabs = [
    {
      value: "",
      label: "Alle Fahrschüler",
      content: (
        <DrivingSchoolStudentsList
          key="students"
          storeKey="drivingSchoolStudents"
          filter={{ archived: false }}
          empty={<EmptyState />}
        />
      ),
    },
    {
      value: "studentsWithoutInstructors",
      label: "Warten auf Zuweisung",
      badge:
        numberOfStudentsWithoutAnInstructor > 0 ? (
          <Box sx={{ mt: "1px" }}>
            <Badge2 badgeContent={numberOfStudentsWithoutAnInstructor} className="Warten-auf-Zuweisung-Badge" />
          </Box>
        ) : undefined,
      content: (
        <DrivingSchoolStudentsList
          key="studentsWithoutInstructors"
          storeKey="studentsWithoutInstructors"
          filter={studentsWithoutAnInstructorFilter}
          empty={
            <EmptyState
              noEntriesText="Alle Fahrschüler sind einem Fahrlehrer zugeordnet."
              noMatchingEntriesText="Keine passenden Fahrschüler gefunden."
            />
          }
        />
      ),
    },
    {
      value: "studentsWithNegativeBalance",
      label: "Mit negativem Saldo",
      content: (
        <DrivingSchoolStudentsList
          key="studentsWithNegativeBalance"
          storeKey="studentsWithNegativeBalance"
          filter={{ archived: false, balance: (val: number) => val < 0 }}
          empty={
            <EmptyState
              noEntriesText="Kein Fahrschüler mit negativem Saldo vorhanden."
              noMatchingEntriesText="Keine passenden Fahrschüler gefunden."
            />
          }
        />
      ),
    },
    {
      value: "studentsArchived",
      label: "Archiv",
      content: (
        <DrivingSchoolStudentsList
          key="studentsArchived"
          storeKey="studentsArchived"
          filter={{ archived: true }}
          empty={
            <EmptyState
              noEntriesText="Kein archivierter Fahrschüler vorhanden."
              noMatchingEntriesText="Keine passenden Fahrschüler gefunden."
            />
          }
        />
      ),
    },
    ...(showVogelFahrschuelerList
      ? [
          {
            value: "vogelFahrschulmanager",
            label: "Vogel Fahrschulmanager",
            content: <LazyVogelFahrschuelerList />,
          },
        ]
      : []),
  ];

  return (
    <div style={{ maxWidth: "100%", minWidth: 0, overflowX: "hidden", position: "relative", width: "100%" }}>
      <Row justifyContent="space-between" alignItems="center" sx={{ mb: "30px", minHeight: "32px" }}>
        <PageTitle documentTitle="Fahrschüler">
          <h2 style={{ margin: 0, lineHeight: 0.83 }}>Fahrschüler</h2>
        </PageTitle>
        <Row spacing={1}>
          {grants.has("Student:ShowCreateButton") && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate("/students/create")}
              size="small"
            >
              Hinzufügen
            </Button>
          )}
        </Row>
      </Row>
      <ResponsiveHashTabs
        tabs={tabs.map((tab) => ({
          ...tab,
          mobileLabel:
            tab.value === "studentsWithoutInstructors" && numberOfStudentsWithoutAnInstructor > 0
              ? `${tab.label} (${numberOfStudentsWithoutAnInstructor})`
              : tab.label,
        }))}
        mobileSelectAriaLabel="Fahrschüler-Ansicht"
      />
    </div>
  );
}

const LazyVogelFahrschuelerList = lazy(() =>
  import("../../lists/VogelFahrschuelerList").then((module) => ({ default: module.VogelFahrschuelerList })),
);
